import type * as React from "react";
import { ErrorBannerValue } from "../errorBanner";

// Imperative API for the global error banner, kept in a sidecar (not ErrorBanner.tsx) so the
// component file exports only components — otherwise Fast Refresh breaks and
// react-refresh/only-export-components fires. ErrorBannerContainer registers its state setter
// here on mount; the functions below are safe no-ops until it does.
export type ErrorBannerStateProps = {
  type: ErrorBannerValue | null;
  refreshUrl?: string;
  // The caller already captured this incident to Sentry with app_broken tags on the
  // exception itself (see unhandledAppBrokenHint), so the banner must not emit its own
  // captureAppBrokenMessage — that would double-count the incident as two events.
  alreadyReported?: boolean;
  // Normalized route (`normalizeAppBrokenRoute`) the failure happened on, tagged as
  // `app_broken_route`. Telemetry only — never rendered.
  route?: string;
};

let _setBannerProps: React.Dispatch<React.SetStateAction<ErrorBannerStateProps>> | undefined;

export const registerErrorBannerSetter = (
  setter: React.Dispatch<React.SetStateAction<ErrorBannerStateProps>>,
): void => {
  _setBannerProps = setter;
};

export const showErrorBanner = (
  errorType: ErrorBannerValue,
  refreshUrl?: string,
  opts?: { alreadyReported?: boolean; route?: string },
): void => {
  if (!_setBannerProps) return;
  _setBannerProps({ type: errorType, refreshUrl, alreadyReported: opts?.alreadyReported, route: opts?.route });
};

// Clears the banner ONLY if the currently-shown value matches — never clobbers an unrelated
// apiDown/loggedOut banner that happens to be up. Used by realtime auto-dismiss on recovery.
export const hideErrorBanner = (errorType: ErrorBannerValue): void => {
  if (!_setBannerProps) return;
  _setBannerProps((prev) => (prev.type === errorType ? { ...prev, type: null } : prev));
};
